import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { NotificationService } from './notification.service';
import { environment } from 'src/environments/environment';
import { GurdServiceService } from './gurd-service.service';
import { IAlarm, ILocationChanged, ISubUser } from '../shared/models/signalR-model';
@Injectable({
  providedIn: 'root'
})

export class SignalrService {

  private baseUrlHub = this._baseUrlHub;

  private iSystemUser;
  private strUserPassword;

  private hubConnection: HubConnection;

  private _isHubConnected: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isHubConnected$ = this._isHubConnected.asObservable();

  private _isLiveLocationTracking: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isLiveLocationTracking$ = this._isLiveLocationTracking.asObservable();

  private _alarmSubject = new Subject<IAlarm>();
  alarm$ = this._alarmSubject.asObservable();

  private _forceSignOutSubject = new Subject<ISubUser>();
  forceSignOut$ = this._forceSignOutSubject.asObservable();

  private _subUserConnectedSubject = new Subject<ISubUser>();
  subUserConnected$ = this._subUserConnectedSubject.asObservable();

  private _subUserDisconnectedSubject = new Subject<ISubUser>();
  subUserDisconnected$ = this._subUserDisconnectedSubject.asObservable();

  private _locationChangedSubject = new Subject<ILocationChanged>();
  locationChanged$ = this._locationChangedSubject.asObservable();

  private _locationChangedBatchSubject = new Subject<ILocationChanged[]>();
  locationChangedBatch$ = this._locationChangedBatchSubject.asObservable();

  private _hubErrorSubject = new Subject<any>();
  hubError$ = this._hubErrorSubject.asObservable();

  constructor(
    private _tooltip: NotificationService,
    private authService: GurdServiceService,
  ) { }

  get _baseUrlHub(): string {
    return environment.baseUrlHub
      ? `${environment.baseUrlHub.replace(/\/+$/, '')}/fleetyarhub`
      : '';
  }

  public init() {

    this.iSystemUser = this.authService.decrypt(localStorage.getItem('ID') ?? null);
    this.strUserPassword = this.authService.decrypt(localStorage.getItem('pass') ?? null);

    if (this.baseUrlHub == null || this.baseUrlHub == undefined || this.baseUrlHub == '') {
      console.warn('baseUrlHub is not set. Please check your configuration.');
      return;
    }

    if (this.hubConnection && this.hubConnection?.state === 'Connected') {
      console.log('Hub is already connected');
      return;
    }

    if (!this.iSystemUser || !this.strUserPassword) {
      const storedId = localStorage.getItem('ID');
      const storedPass = localStorage.getItem('pass');

      this.iSystemUser = storedId ? this.authService.decrypt(storedId) : null;
      this.strUserPassword = storedPass ? this.authService.decrypt(storedPass) : null;
    }

    if (!this.iSystemUser || !this.strUserPassword) {
      console.error('Missing credentials after recovery attempt', {
        iSystemUser: this.iSystemUser,
        hasPassword: !!this.strUserPassword
      });

      this.updateHubStatus(false);
      return;
    }

    this.startConnection();

  }

  private startConnection() {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(this.baseUrlHub + `?iSystemUser=${this.iSystemUser}&strUserPassword=${this.strUserPassword}`)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build();
    this.registerConnectionEvents();
    this.registerHubListeners();

    this.hubConnection
      .start()
      .then(() => {
        this.updateHubStatus(true);
        console.log('Connection started');
        this._tooltip.showSuccess('اتصال موفق به هاب')

      }).catch(err => {
        this.updateHubStatus(false);
        console.log('Error while starting connection: ' + err)
        this._tooltip.showError('عدم امکان اتصال به هاب');
      });
  }

  private registerConnectionEvents() {

    this.hubConnection.onclose((error) => {
      console.log('Hub disconnected', error);
      this.updateHubStatus(null);
      // this._tooltip.showWarning('ارتباط لحظه ایی شما قطع شده است.', 'متصل نیستید');
    });

    this.hubConnection.onreconnecting((error) => {
      console.log('Hub reconnecting', error);
      this.updateHubStatus(false);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('Hub reconnected', connectionId);
      this.updateHubStatus(true);
      // this._tooltip.showInfo('ارتباط دوباره برقرار شد', '');
    });

  }

  private registerHubListeners() {

    this.registerAlarmListener();
    this.registerForceSignOutListener();
    this.registerSubUserConnectedListener();
    this.registerSubUserDisconnectedListener();
    this.registerLocationChangedListener();
    this.registerLocationChangedBatchListener();
    this.registerHubErrorListener();

  }

  private updateHubStatus(isConnect: boolean) {
    this._isHubConnected.next(isConnect)
  }

  public async reconnect() {
    if (this.hubConnection.state !== 'Disconnected') return;

    try {
      await this.hubConnection.start();
      this.updateHubStatus(true);

    } catch { }
  }

  public async disconnect() {
    try {
      if (this.hubConnection) {
        await this.hubConnection.stop();
        this.updateHubStatus(false);
        console.log('Hub connection stopped');
      }
    } catch (err) {
      this.updateHubStatus(false);
      console.error('Error while stopping hub:', err);
    }
  }

  // فراخوانی هاب های سمت کلاینت

  private registerForceSignOutListener() {
    this.hubConnection.on('HC_ForceSignOut', (model: ISubUser) => {
      this._forceSignOutSubject.next(model);
    })
  }

  private registerSubUserConnectedListener() {
    this.hubConnection.on('HC_OnSubUserConnected', (model: ISubUser) => {
      this._subUserConnectedSubject.next(model);
    })
  }

  private registerSubUserDisconnectedListener() {
    this.hubConnection.on('HC_OnSubUserDisconnected', (model: any) => {
      this._subUserDisconnectedSubject.next(model);
    })
  }

  private registerAlarmListener() {

    this.hubConnection.on('HC_MobileAlarmTriger', (model: IAlarm) => {

      this._alarmSubject.next(model);

    })

  }

  private registerLocationChangedListener() {
    this.hubConnection.on('HC_MobileLocationChanged', (model: ILocationChanged) => {
      if (this.isLocationTrackingActive() == false) return;
      this._locationChangedSubject.next(model);
    })

  }

  private registerLocationChangedBatchListener() {
    this.hubConnection.on('HC_MobileLocationChangedBatch', (models: ILocationChanged[]) => {
      if (this.isLocationTrackingActive() == false) return;
      this._locationChangedBatchSubject.next(models)
    })

  }

  private registerHubErrorListener() {
    this.hubConnection.on('HC_HubError', (model: any) => {
      this._hubErrorSubject.next(model);
    });

  }

  // utility function

  private isLocationTrackingActive(): boolean {

    return this._isLiveLocationTracking.value;

  }

  public updateLiveLocation(isTracking: boolean) {
    this._isLiveLocationTracking.next(isTracking)
  }


}
